import { hasObjectType } from "@/utils/typePredicates";

/**
 * Centralized Query Key Factory
 *
 * All query keys are generated through this factory to ensure:
 * 1. Type safety - Full TypeScript support for all keys
 * 2. Consistency - Single source of truth for key structure
 * 3. Tenant isolation - Company-scoped keys are grouped under company prefix
 * 4. Easy invalidation - Hierarchical structure allows invalidating entire scopes
 * 5. Stable serialization - Deep, deterministic serialization for filters
 *
 * Usage:
 *   queryKeys.company(companyId).agents()           // ['company', companyId, 'agents']
 *   queryKeys.company(companyId).services().jobTypes() // ['company', companyId, 'services', 'jobTypes']
 *   queryKeys.global.appFeatures()                  // ['global', 'appFeatures']
 */

import type { CallHistoryFilters } from "@libs/Phone/calls/types";
import type { PhoneDashboardHistoryParams } from "@libs/Phone/dashboard/range";
// Type-only, so this factory stays a runtime leaf: the edge back to the
// calendar hooks is erased at compile time rather than forming an import cycle.
import type { CalendarApiPath } from "./useCalendarIntegration";

type QueryKeyValue =
  | boolean
  | null
  | number
  | string
  | undefined
  | readonly QueryKeyValue[]
  | QueryKeyFilters;

interface QueryKeyFilters {
  [key: string]: QueryKeyValue;
}

const isQueryKeyArray = (
  value: QueryKeyValue,
): value is readonly QueryKeyValue[] => Array.isArray(value);

const isQueryKeySegmentArray = (
  value: string | readonly string[] | null | undefined,
): value is readonly string[] => Array.isArray(value);

/**
 * Deep stable JSON serialization for filter objects
 * - Recursively sorts object keys at all levels
 * - Handles arrays (preserves order, recursively serializes elements)
 * - Omits undefined values entirely
 * - Returns null (not undefined) when input is empty/undefined
 *
 */
const normalizeQueryKeyValue = (
  value: QueryKeyValue,
  preserveNull: boolean,
): QueryKeyValue | undefined => {
  if (value === undefined || (value === null && !preserveNull)) {
    return undefined;
  }

  if (isQueryKeyArray(value)) {
    const normalized = value.flatMap((item) => {
      const normalizedItem = normalizeQueryKeyValue(item, false);
      return normalizedItem === undefined ? [] : [normalizedItem];
    });
    return normalized.length > 0 ? normalized : undefined;
  }

  if (hasObjectType(value) && value !== null) {
    const sorted: QueryKeyFilters = {};
    for (const key of Object.keys(value).sort()) {
      const normalizedValue = normalizeQueryKeyValue(value[key], true);
      if (normalizedValue !== undefined) {
        sorted[key] = normalizedValue;
      }
    }
    return Object.keys(sorted).length > 0 ? sorted : undefined;
  }

  return value;
};

const deepStableSerialize = (
  value: QueryKeyValue | undefined,
): string | null => {
  const normalized =
    value === undefined || value === null
      ? undefined
      : normalizeQueryKeyValue(value, false);
  return normalized === undefined ? null : JSON.stringify(normalized);
};

/**
 * Helper to build query key with optional serialized filters
 * Omits null/undefined segments to keep keys clean
 */
const buildKey = (
  ...segments: (string | readonly string[] | null | undefined)[]
): readonly string[] => {
  const result: string[] = [];
  for (const segment of segments) {
    if (segment === null || segment === undefined) continue;
    if (isQueryKeySegmentArray(segment)) {
      result.push(...segment);
    } else {
      result.push(segment);
    }
  }
  return result;
};

/**
 * Placeholder key for disabled queries
 * React Query requires a queryKey even when enabled: false
 */
export const DISABLED_KEY = ["_disabled"] as const;

export const queryKeys = {
  /**
   * Company-scoped queries
   * All data that belongs to a specific tenant
   */
  company: (companyId: string) => ({
    // Base key for all company data - use for bulk invalidation on company switch
    all: ["company", companyId] as const,

    // Company details
    details: () => buildKey(queryKeys.company(companyId).all, "details"),
    provisioning: () =>
      buildKey(queryKeys.company(companyId).all, "provisioning"),
    // Super-only teardown audit run (SD-17404).
    teardown: () => buildKey(queryKeys.company(companyId).all, "teardown"),
    dispatchFeatureAccess: () =>
      buildKey(queryKeys.company(companyId).all, "dispatchFeatureAccess"),
    reportSettings: () =>
      buildKey(queryKeys.company(companyId).all, "reportSettings"),
    reportSubscriptions: () =>
      buildKey(queryKeys.company(companyId).all, "reportSubscriptions"),
    notificationPreferences: () =>
      buildKey(queryKeys.company(companyId).all, "notificationPreferences"),
    // Its own leaf, not under `notificationPreferences`: a save invalidates
    // that roster prefix, and the catalog never changes on a save.
    notificationPreferencesSchema: () =>
      buildKey(
        queryKeys.company(companyId).all,
        "notificationPreferencesSchema",
      ),
    phoneExpansion: () =>
      buildKey(queryKeys.company(companyId).all, "phoneExpansion"),
    // Super-hub test identities minted into this demo company (SD-15956).
    demoIdentities: () =>
      buildKey(queryKeys.company(companyId).all, "demoIdentities"),

    // Operator work surface (SD-11166): durable tasks, findings, parked
    // proposals and automations, read by the Operator chat and Review.
    operator: () => buildKey(queryKeys.company(companyId).all, "operator"),
    operatorTasks: (status?: string) =>
      buildKey(
        queryKeys.company(companyId).operator(),
        "tasks",
        status ?? "all",
      ),
    operatorTask: (taskId: string) =>
      buildKey(queryKeys.company(companyId).operatorTasks(), taskId),
    operatorAgents: () =>
      buildKey(queryKeys.company(companyId).operator(), "agents"),
    operatorRelics: () =>
      buildKey(queryKeys.company(companyId).operator(), "relics"),
    operatorRelic: (id: string) =>
      buildKey(queryKeys.company(companyId).operatorRelics(), "detail", id),
    operatorRecipes: () =>
      buildKey(queryKeys.company(companyId).operator(), "recipes"),
    operatorAutomations: () =>
      buildKey(queryKeys.company(companyId).operator(), "automations"),
    operatorAutomation: (automationId: string) =>
      buildKey(
        queryKeys.company(companyId).operatorAutomations(),
        automationId,
      ),
    operatorAutomationRunRequest: (
      automationId: string,
      occurrenceId: string,
    ) =>
      buildKey(
        queryKeys.company(companyId).operatorAutomation(automationId),
        "run-requests",
        occurrenceId,
      ),
    operatorAutomationOccurrences: (automationId: string) =>
      buildKey(
        queryKeys.company(companyId).operatorAutomation(automationId),
        "occurrences",
      ),
    operatorRuns: () =>
      buildKey(queryKeys.company(companyId).operator(), "runs"),
    operatorRun: (runId: string) =>
      buildKey(queryKeys.company(companyId).operatorRuns(), runId),
    operatorProposals: (status?: string) =>
      buildKey(
        queryKeys.company(companyId).operator(),
        "proposals",
        status ?? "all",
      ),
    operatorProposal: (proposalId: string) =>
      buildKey(queryKeys.company(companyId).operatorProposals(), proposalId),
    operatorFindings: () =>
      buildKey(queryKeys.company(companyId).operator(), "findings"),
    // The signed-in user's private Operator chats. Items are siblings of the
    // list, not children, so refreshing the list never refetches a chat's items.
    operatorChats: () =>
      buildKey(queryKeys.company(companyId).operator(), "chats"),
    operatorChatList: () =>
      buildKey(queryKeys.company(companyId).operatorChats(), "list"),
    operatorChatItems: (chatId: string) =>
      buildKey(queryKeys.company(companyId).operatorChats(), chatId, "items"),
    // One chat as the list pages hold it, read-only ones included.
    operatorChat: (chatId: string) =>
      buildKey(queryKeys.company(companyId).operatorChats(), chatId, "chat"),
    // The conversation a run's Task writes into, found through the Task.
    operatorChatForTask: (taskId: string) =>
      buildKey(queryKeys.company(companyId).operatorChats(), "task", taskId),

    // Internal SEO workspace for Sameday's marketing site. Invalidate
    // `seo().all` to refresh the whole workspace at once.
    seo: () => ({
      all: buildKey(queryKeys.company(companyId).all, "seo"),
      overview: () =>
        buildKey(queryKeys.company(companyId).seo().all, "overview"),
      rankings: () =>
        buildKey(queryKeys.company(companyId).seo().all, "rankings"),
      audit: () => buildKey(queryKeys.company(companyId).seo().all, "audit"),
      keywords: () =>
        buildKey(queryKeys.company(companyId).seo().all, "keywords"),
      // SEO workspace (SD-11744): research + competitor/backlink analysis.
      serp: (keyword: string) =>
        buildKey(queryKeys.company(companyId).seo().all, "serp", keyword),
      savedKeywords: () =>
        buildKey(queryKeys.company(companyId).seo().all, "savedKeywords"),
      domainAnalysis: (domain: string) =>
        buildKey(
          queryKeys.company(companyId).seo().all,
          "domainAnalysis",
          domain,
        ),
      backlinks: () =>
        buildKey(queryKeys.company(companyId).seo().all, "backlinks"),
      // Wave A2 (SD-11746): local SEO, competitor discovery, audit pages.
      localSerp: (keyword: string) =>
        buildKey(queryKeys.company(companyId).seo().all, "localSerp", keyword),
      localBusinesses: (query: string, category: string) =>
        buildKey(
          queryKeys.company(companyId).seo().all,
          "localBusinesses",
          query,
          category,
        ),
      businessQuestions: (keyword: string) =>
        buildKey(
          queryKeys.company(companyId).seo().all,
          "businessQuestions",
          keyword,
        ),
      serpCompetitors: () =>
        buildKey(queryKeys.company(companyId).seo().all, "serpCompetitors"),
      auditPages: (auditId: string) =>
        buildKey(queryKeys.company(companyId).seo().all, "auditPages", auditId),
      // Wave B (SD-11749): Google Search Console connection + performance.
      googleConnection: () =>
        buildKey(queryKeys.company(companyId).seo().all, "googleConnection"),
      searchPerformanceAll: () =>
        buildKey(queryKeys.company(companyId).seo().all, "searchPerformance"),
      searchPerformance: (range: string, dimension: string) =>
        buildKey(
          queryKeys.company(companyId).seo().searchPerformanceAll(),
          range,
          dimension,
        ),
      urlInspectionAll: () =>
        buildKey(queryKeys.company(companyId).seo().all, "urlInspection"),
      urlInspection: (url: string) =>
        buildKey(queryKeys.company(companyId).seo().urlInspectionAll(), url),
    }),

    // Agents
    agents: () => buildKey(queryKeys.company(companyId).all, "agents"),
    agent: (agentId: string) =>
      buildKey(queryKeys.company(companyId).agents(), agentId),
    agentFullDuplexVoice: (agentId: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "agentFullDuplexVoice",
        agentId,
      ),
    agentPrompt: (agentId: string) =>
      buildKey(queryKeys.company(companyId).agent(agentId), "prompt"),
    promptsBySection: (section: string, agentId?: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "promptsBySection",
        section,
        agentId ?? "",
      ),

    // Conversations (with deep stable filter serialization)
    conversations: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "conversations",
        deepStableSerialize(filters),
      ),
    conversation: (id: string) =>
      buildKey(queryKeys.company(companyId).all, "conversations", "detail", id),
    // Phone System conversations have a separate data contract from the
    // existing inbox. Keep their list and detail cache trees distinct so a
    // Phone System row can never satisfy a normal inbox query (SD-14270).
    phoneSystemInteractions: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneSystemInteractions",
        deepStableSerialize(filters),
      ),
    phoneSystemInteraction: (id: string) =>
      buildKey(
        queryKeys.company(companyId).phoneSystemInteractions(),
        "detail",
        id,
      ),
    phoneSystemSmsTimeline: (id: string) =>
      buildKey(
        queryKeys.company(companyId).phoneSystemInteractions(),
        "smsTimeline",
        id,
      ),
    conversationTeamNotifications: (id: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "conversations",
        "detail",
        id,
        "teamNotifications",
      ),
    newConversationsCount: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "conversations",
        "newCount",
        deepStableSerialize(filters),
      ),
    semanticSearch: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "semanticSearch",
        deepStableSerialize(filters),
      ),

    // Company activity feed (with deep stable filter serialization). The
    // keyset cursor stays out of the key — useInfiniteQuery threads it through
    // pageParam so each filter permutation owns one growing infinite cache.
    activity: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "activity",
        deepStableSerialize(filters),
      ),

    // Grouped activity feed (`GET /activity/grouped`). Separate leaf from the
    // flat feed so grouped pages and flat expansion fetches never collide.
    activityGrouped: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "activityGrouped",
        deepStableSerialize(filters),
      ),

    /** Company-wide HubSpot tickets list (API is not conversation-scoped). */
    hubspotTickets: () =>
      buildKey(queryKeys.company(companyId).all, "hubspotTickets"),

    // In-app notifications (key shape: [company, cid, "notifications", <leaf>]).
    // Invalidate the [company, cid, "notifications"] prefix to refresh every
    // list filter + the unread count together.
    notifications: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "notifications",
        deepStableSerialize(filters),
      ),
    notificationsUnreadCount: () =>
      buildKey(
        queryKeys.company(companyId).all,
        "notifications",
        "unreadCount",
      ),

    // Campaigns
    campaigns: () => buildKey(queryKeys.company(companyId).all, "campaigns"),
    campaign: (id: string) =>
      buildKey(queryKeys.company(companyId).campaigns(), id),
    campaignAudience: (campaignId: string, filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "audience",
        deepStableSerialize(filters),
      ),
    campaignAudienceExecution: (campaignId: string, executionId: string) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "audience",
        executionId,
      ),
    campaignStats: (campaignId: string, filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "stats",
        deepStableSerialize(filters),
      ),
    campaignGoalsTimeseries: (campaignId: string, filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "stats",
        "timeseries",
        deepStableSerialize(filters),
      ),
    campaignBestContactTimes: (campaignId: string, filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "stats",
        "best-contact-times",
        deepStableSerialize(filters),
      ),
    campaignIntegrationLeads: (campaignId: string, filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "integrationLeads",
        deepStableSerialize(filters),
      ),
    campaignLaunchReadiness: (campaignId: string) =>
      buildKey(queryKeys.company(companyId).campaign(campaignId), "readiness"),
    campaignBulkResumePausedEligibility: (campaignId: string) =>
      buildKey(
        queryKeys.company(companyId).campaign(campaignId),
        "bulkResumePausedEligibility",
      ),

    // Services
    services: () => ({
      all: buildKey(queryKeys.company(companyId).all, "services"),
      pricingBookSummary: () =>
        buildKey(
          queryKeys.company(companyId).services().all,
          "pricingBookSummary",
        ),
      pricingBookRules: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).services().all,
          "pricingBookRules",
          deepStableSerialize(filters),
        ),
      tokenExpansions: () =>
        buildKey(
          queryKeys.company(companyId).services().all,
          "tokenExpansions",
        ),
      jobTypes: () =>
        buildKey(queryKeys.company(companyId).services().all, "jobTypes"),
      jobTags: () =>
        buildKey(queryKeys.company(companyId).services().all, "jobTags"),
      callReasons: () =>
        buildKey(queryKeys.company(companyId).services().all, "callReasons"),
      customFields: () =>
        buildKey(queryKeys.company(companyId).services().all, "customFields"),
      membershipTypes: () =>
        buildKey(
          queryKeys.company(companyId).services().all,
          "membershipTypes",
        ),
      businessUnits: () =>
        buildKey(queryKeys.company(companyId).services().all, "businessUnits"),
      offices: () =>
        buildKey(queryKeys.company(companyId).services().all, "offices"),
      arrivalWindows: () =>
        buildKey(queryKeys.company(companyId).services().all, "arrivalWindows"),
      zones: () =>
        buildKey(queryKeys.company(companyId).services().all, "zones"),
      forwarderRules: () =>
        buildKey(queryKeys.company(companyId).services().all, "forwarderRules"),
      serviceTitanCampaigns: () =>
        buildKey(queryKeys.company(companyId).services().all, "stCampaigns"),
      pricingCatalogInfo: () =>
        buildKey(
          queryKeys.company(companyId).services().all,
          "pricingCatalogInfo",
        ),
    }),

    // Technicians
    technicians: () =>
      buildKey(queryKeys.company(companyId).all, "technicians"),
    technician: (id: string) =>
      buildKey(queryKeys.company(companyId).technicians(), id),
    // Pass a `week` key (e.g. the viewed week's ISO Monday) to scope the cache
    // per week, and an optional `filters` object (BU/technician/shift-type) so
    // different filter sets don't collide. Empty/undefined filters serialize to
    // null and are dropped, keeping the unfiltered key unchanged. Called with no
    // args, it returns the prefix shared by every week, so mutation
    // `invalidateQueries` still refreshes whichever week/filter is cached.
    technicianShifts: (week?: string, filters?: QueryKeyFilters) => {
      const base = buildKey(
        queryKeys.company(companyId).all,
        "technicianShifts",
      );
      const withWeek = week ? buildKey(base, week) : base;
      return buildKey(withWeek, deepStableSerialize(filters));
    },

    // Availability
    availability: () =>
      buildKey(queryKeys.company(companyId).all, "availability"),
    jobTypesAvailability: () =>
      buildKey(queryKeys.company(companyId).all, "jobTypesAvailability"),

    // Search
    searchPhoneNumbers: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "searchPhoneNumbers",
        deepStableSerialize(filters),
      ),
    searchCampaignItems: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "searchCampaignItems",
        deepStableSerialize(filters),
      ),
    jobTypeSkills: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "jobTypeSkills",
        deepStableSerialize(filters),
      ),
    brandStatus: () =>
      buildKey(queryKeys.company(companyId).all, "brandStatus"),
    cancelReasons: () =>
      buildKey(queryKeys.company(companyId).all, "cancelReasons"),
    nativeAdcapStatus: () =>
      buildKey(queryKeys.company(companyId).all, "nativeAdcapStatus"),
    nativeCallRequests: (callId: string) =>
      buildKey(queryKeys.company(companyId).all, "nativeCallRequests", callId),

    // Coach (with deep stable filter serialization)
    coach: () => ({
      all: buildKey(queryKeys.company(companyId).all, "coach"),
      config: () =>
        buildKey(queryKeys.company(companyId).coach().all, "config"),
      departments: () =>
        buildKey(queryKeys.company(companyId).coach().all, "departments"),
      entityCandidates: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "entityCandidates",
          deepStableSerialize(filters),
        ),
      employees: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "employees",
          deepStableSerialize(filters),
        ),
      externalConversations: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "externalConversations",
          deepStableSerialize(filters),
        ),
      agents: () =>
        buildKey(queryKeys.company(companyId).coach().all, "agents"),
      reportSettings: () =>
        buildKey(queryKeys.company(companyId).coach().all, "reportSettings"),
      linkedAgents: (userId: string) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "linkedAgents",
          userId,
        ),
      agentDetails: (agentId: string, days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "agentDetails",
          agentId,
          String(days),
        ),
      agentPerformance: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "agentPerformance",
          deepStableSerialize(filters),
        ),
      agentRanking: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "agentRanking",
          String(days),
        ),
      // Dashboard metrics
      playbookAccuracy: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "playbookAccuracy",
          String(days),
        ),
      dashboardStats: (days: number, useProjection?: boolean) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "dashboardStats",
          String(days),
          String(useProjection ?? true),
        ),
      callRecording: (callId: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "callRecording",
          String(callId),
        ),
      conversationAudio: (conversationId: string) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "conversationAudio",
          conversationId,
        ),
      dailyCallVolume: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "dailyCallVolume",
          String(days),
        ),
      dailyCallDuration: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "dailyCallDuration",
          String(days),
        ),
      dailyAnalyzedCalls: (days: number, conversationSource?: string) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "dailyAnalyzedCalls",
          String(days),
          conversationSource ?? "",
        ),
      bookingConversion: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "bookingConversion",
          String(days),
        ),
      conversationSummary: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "conversationSummary",
          deepStableSerialize(filters),
        ),
      conversationDetails: (conversationId: string) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "conversationDetails",
          conversationId,
        ),
      companyDetectionsSummary: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "companyDetectionsSummary",
          String(days),
        ),
      callSyncStatus: (integrationSource?: string) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "callSyncStatus",
          integrationSource ?? "servicetitan",
        ),
      callDirectionStats: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "callDirectionStats",
          String(days),
        ),
      bundledDashboard: (
        timeRange: string,
        conversationSource?: string,
        departmentIds?: string,
        startDate?: string,
        endDate?: string,
      ) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "bundledDashboard",
          timeRange,
          conversationSource ?? "all",
          departmentIds,
          // Custom date windows must enter the cache key — without them
          // two different custom windows would share one ``"custom"``
          // entry and stale data would surface across selections.
          startDate,
          endDate,
        ),
      criterionPerformance: (
        comparisonCohort: string,
        timeRange: string,
        direction?: string,
        departmentIds?: string,
        agentIds?: string,
        startDate?: string,
        endDate?: string,
      ) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "criterionPerformance",
          comparisonCohort,
          timeRange,
          direction ?? "both",
          departmentIds,
          agentIds,
          // Custom date windows must enter the cache key so two windows don't
          // share one "custom" entry (see bundledDashboard).
          startDate,
          endDate,
        ),
      analyzedCallsHangupTimeline: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "analyzedCallsHangupTimeline",
          String(days),
        ),
      allCallsHangupTimeline: (days: number) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "allCallsHangupTimeline",
          String(days),
        ),
      teamPerformance: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "teamPerformance",
          deepStableSerialize(filters),
        ),
      allPlaybookStepCalls: (
        timeRange: string,
        showMissedOnly?: boolean,
        conversationSource?: string,
        departmentIds?: string,
      ) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "allPlaybookStepCalls",
          timeRange,
          String(showMissedOnly ?? true),
          conversationSource ?? "all",
          departmentIds,
        ),
      reviewCalls: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "reviewCalls",
          deepStableSerialize(filters),
        ),
      coachConversationsTable: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "coachConversationsTable",
          deepStableSerialize(filters),
        ),
      infiniteCoachConversationsTable: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).coach().all,
          "infiniteCoachConversationsTable",
          deepStableSerialize(filters),
        ),
    }),

    // Adaptive Capacity — Cursor availability (SD-8797)
    cursor: () => ({
      all: buildKey(queryKeys.company(companyId).all, "cursor"),
      tuples: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).cursor().all,
          "tuples",
          deepStableSerialize(filters),
        ),
      tupleDays: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).cursor().all,
          "tupleDays",
          deepStableSerialize(filters),
        ),
      zones: () => buildKey(queryKeys.company(companyId).cursor().all, "zones"),
      assess: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.company(companyId).cursor().all,
          "assess",
          deepStableSerialize(filters),
        ),
      seedJob: (jobId: string) =>
        buildKey(queryKeys.company(companyId).cursor().all, "seedJob", jobId),
      seedJobs: () =>
        buildKey(queryKeys.company(companyId).cursor().all, "seedJobs"),
    }),

    // Integrations
    integrations: () => ({
      all: buildKey(queryKeys.company(companyId).all, "integrations"),
      connectionStatus: () =>
        buildKey(queryKeys.company(companyId).integrations().all, "status"),
      availableActions: (type: string) =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "actions",
          type,
        ),
      thumbtack: () =>
        buildKey(queryKeys.company(companyId).integrations().all, "thumbtack"),
      voiceForPestSip: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "voice_for_pest_sip",
        ),
      // Keyed by the provider's hyphenated API path, so two calendar providers
      // connected to one company never share a cache entry.
      calendar: (apiPath: CalendarApiPath) =>
        buildKey(queryKeys.company(companyId).integrations().all, apiPath),
      // A prefix extension of `calendar()`, so invalidating the connection
      // after a connect or disconnect re-probes the health verdict too — which
      // is stale by definition at that point.
      calendarHealth: (apiPath: CalendarApiPath) =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          apiPath,
          "health",
        ),
      // Prefix extensions of `calendar()` too: the calendars a grant can see
      // and the settings pointing at them are both scoped to that grant, so a
      // connect or disconnect must drop them with it.
      calendarList: (apiPath: CalendarApiPath) =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          apiPath,
          "calendars",
        ),
      calendarSettings: (apiPath: CalendarApiPath) =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          apiPath,
          "settings",
        ),
      // Keyed by the searched window: availability read for one window says
      // nothing about another, and editing the dates must drop the previous
      // answer rather than show it under the new inputs.
      calendarAvailability: (
        apiPath: CalendarApiPath,
        fromDate: string,
        toDate: string,
      ) =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          apiPath,
          "find-spot",
          fromDate,
          toDate,
        ),
      googleLsa: () =>
        buildKey(queryKeys.company(companyId).integrations().all, "google-lsa"),
      yelp: () =>
        buildKey(queryKeys.company(companyId).integrations().all, "yelp"),
      leadperfection: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "leadperfection",
        ),
      houseCallProHealth: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "housecallpro",
          "health",
        ),
      houseCallProEmergencyReadiness: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "housecallpro",
          "emergency-scheduling-readiness",
        ),
      houseCallProScheduleWindows: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "housecallpro",
          "schedule-windows",
        ),
      fieldRoutesHealth: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "fieldroutes",
          "health",
        ),
      serviceTitanHealth: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "servicetitan",
          "health",
        ),
      serviceFusionHealth: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "servicefusion",
          "health",
        ),
      watscoOcaOutboundApi: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "watsco_oca",
          "outbound_api",
        ),
      maidcentralHealth: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "maidcentral",
          "health",
        ),
      maidcentralScopeGroups: () =>
        buildKey(
          queryKeys.company(companyId).integrations().all,
          "maidcentral",
          "scopeGroups",
        ),
      // Zernio social integrations (accounts / capabilities / select options)
      zernio: () => ({
        all: buildKey(
          queryKeys.company(companyId).integrations().all,
          "zernio",
        ),
        accounts: () =>
          buildKey(
            queryKeys.company(companyId).integrations().zernio().all,
            "accounts",
          ),
        capabilities: (platform: string) =>
          buildKey(
            queryKeys.company(companyId).integrations().zernio().all,
            "capabilities",
            platform,
          ),
        selectOptions: (platform: string) =>
          buildKey(
            queryKeys.company(companyId).integrations().zernio().all,
            "selectOptions",
            platform,
          ),
      }),
    }),

    // Users
    users: () => buildKey(queryKeys.company(companyId).all, "users"),
    fieldSalesUsers: () =>
      buildKey(queryKeys.company(companyId).all, "fieldSales", "users"),
    // Customers
    customers: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.company(companyId).all,
        "customers",
        deepStableSerialize(filters),
      ),
    customerProfile: (phone: string) =>
      buildKey(queryKeys.company(companyId).all, "customerProfile", phone),
    // A caller-context result is scoped to a WebRTC call as well as the phone
    // number. Reusing a prior leg's query key could briefly paint one caller's
    // CRM data while a new call is taking the dock.
    crmCallerContext: (callId: string, phone: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "crmCallerContext",
        callId,
        phone,
      ),
    user: (id: string) => buildKey(queryKeys.company(companyId).users(), id),

    // Phone Numbers
    phoneNumbers: () =>
      buildKey(queryKeys.company(companyId).all, "phoneNumbers"),
    phoneStatus: () =>
      buildKey(queryKeys.company(companyId).all, "phoneStatus"),
    phoneMessagingReadiness: () =>
      buildKey(queryKeys.company(companyId).all, "phoneMessagingReadiness"),
    // Inbox + unread are scoped to the active local sender (SD-13221): the same
    // remote number under two different local numbers is two independent
    // threads. The local number is a key suffix, so `phoneSmsThreads()` (no
    // arg) stays the invalidation prefix over every sender, while
    // `phoneSmsThreads(localNumber)` isolates one sender's cache. A null/omitted
    // local number keys to the bare prefix — the legacy company-shared inbox.
    phoneSmsThreads: (localNumber?: string | null) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneSmsThreads",
        localNumber ?? undefined,
      ),
    phoneSmsUnreadSummary: (localNumber?: string | null) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneSmsUnreadSummary",
        localNumber ?? undefined,
      ),
    phoneSmsThreadTimeline: (threadId: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneSmsThreadTimeline",
        threadId,
      ),
    phoneMessageRecipients: (query: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneMessageRecipients",
        query,
      ),
    phoneMessageRecipientGroups: () =>
      buildKey(queryKeys.company(companyId).all, "phoneMessageRecipientGroups"),
    // Group-send batch status is keyed by its immutable clientBatchId (SD-12795),
    // never by the active dock sender: the batch is bound to the sender persisted
    // at submit time, so polling and retry stay tied to the original send even
    // after the operator switches the active texting number.
    phoneMessageRecipientGroupBatch: (clientBatchId: string) =>
      buildKey(
        queryKeys.company(companyId).all,
        "phoneMessageRecipientGroupBatch",
        clientBatchId,
      ),

    // Service Areas
    serviceAreas: () =>
      buildKey(queryKeys.company(companyId).all, "serviceAreas"),

    // Playbooks & Workflows
    playbooks: () => buildKey(queryKeys.company(companyId).all, "playbooks"),
    playbook: (id: string) =>
      buildKey(queryKeys.company(companyId).playbooks(), id),
    multiAgentConfigs: () =>
      buildKey(queryKeys.company(companyId).all, "multiAgentConfigs"),
    workflowTemplates: () =>
      buildKey(queryKeys.company(companyId).all, "workflowTemplates"),

    // Stats
    stats: () => ({
      all: buildKey(queryKeys.company(companyId).all, "stats"),
      // Report Hub v2 (SD-10758 / SD-11016): keyed under `stats()` so a
      // disposition override's `stats().all` invalidation busts it too.
      inAppReportV2: (preset: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "inAppReportV2",
          preset,
        ),
      inAppReportV2Custom: (start: string, end: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "inAppReportV2",
          "custom",
          start,
          end,
        ),
      // AI Calls report (SD-11454): the v2 report scoped to a single channel.
      // Keyed by window + channel so each channel toggle warms its own cache,
      // and nested under `stats().all` so a disposition/review override's
      // `stats().all` invalidation busts it too.
      aiCallsReport: (
        windowKey: string,
        channel: string,
        // AI Calls agent scope (SD-13046). Trailing with a "-" default so every
        // existing unfiltered key stays byte-identical.
        agentId = "-",
      ) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "aiCallsReport",
          windowKey,
          channel,
          agentId,
        ),
      // New-vs-returning caller mix (T3 / SD-9880): keyed by the window it shares
      // with the aggregate report (preset or custom range), plus the channel it
      // is scoped to (SD-11892) so each channel toggle warms its own cache.
      callerMix: (windowKey: string, channel: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "callerMix",
          windowKey,
          channel,
        ),
      bookingWizardReport: (windowKey: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "bookingWizardReport",
          windowKey,
        ),
      webchatReport: (windowKey: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "webchatReport",
          windowKey,
        ),
      // Company-wide Campaigns dashboard (SD-11585 report, SD-11588 overview).
      // Keyed by window and nested under `stats().all` like the other Report Hub
      // reports so shared invalidations cascade. The report (per-campaign rows)
      // and the overview (tiles + best-contact-times) are separate endpoints and
      // cache under distinct keys.
      campaignReport: (windowKey: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "campaignReport",
          windowKey,
        ),
      campaignOverview: (windowKey: string) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "campaignOverview",
          windowKey,
        ),
      // Booking Wizard session drill-down (SD-11593): keyed by the window plus the
      // metric card's status/step/trade filter so each card's drill-down warms its
      // own cache. Nested under `stats().all` like the other report drill-downs.
      // Default "-" keeps unfiltered (Total Sessions) keys stable.
      bookingWizardSessions: (
        windowKey: string,
        status = "-",
        step = "-",
        trade = "-",
      ) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "bookingWizardSessions",
          windowKey,
          status,
          step,
          trade,
        ),
      // Metric drill-down conversations (T2 / SD-9879): keyed by metric, optional
      // dimension label, the window, and the optional secondary cross-filter
      // (SD-9954).
      metricDrilldown: (
        metricId: string,
        dimensionLabel: string | undefined,
        windowKey: string,
        filterKey: string,
        // AI Calls scope (SD-11454): channel + dimension axis so each channel /
        // axis warms its own drill-down cache. Default "-" keeps legacy keys
        // (no AI Calls scope) stable.
        channel = "-",
        dimensionAxis = "-",
        // AI Calls agent scope (SD-13046), same trailing-default reason: a
        // drill-down must be keyed to the same agent its card was counted for.
        agentId = "-",
        // Conversation type scope (SD-15401). Webchat metrics share registry
        // ids with broader interaction metrics, so omitting this discriminator
        // would let their cached rows collide.
        conversationType = "-",
      ) =>
        buildKey(
          queryKeys.company(companyId).stats().all,
          "metricDrilldown",
          metricId,
          dimensionLabel ?? "-",
          windowKey,
          filterKey,
          channel,
          dimensionAxis,
          agentId,
          conversationType,
        ),
    }),

    // Billing
    billingConfig: () =>
      buildKey(queryKeys.company(companyId).all, "billingConfig"),
    vendorUsage: (month: string) =>
      buildKey(queryKeys.company(companyId).all, "vendorUsage", month),

    // Insights
    insights: () => ({
      all: buildKey(queryKeys.company(companyId).all, "insights"),
      overview: () =>
        buildKey(queryKeys.company(companyId).insights().all, "overview"),
    }),
  }),

  /**
   * Slug → company resolver (`GET /c/{slug}`, SD-11894). Keyed by slug, not id,
   * because the id is exactly what this lookup resolves. Kept distinct from the
   * id-keyed `company(id)` subtree so a company-switch invalidation of
   * `company(id).all` never clobbers a slug lookup, and vice versa.
   *
   * Scoped by `accountId` because a slug is only unique *per account*: an
   * unscoped, slug-only key would both collide two accounts' identically-named
   * slugs AND let a later auth context (a different account signing in before
   * the fresh request lands) read the previous session's access-controlled
   * company from cache.
   */
  companyBySlug: (accountId: string, slug: string) =>
    ["company", "by-slug", accountId, slug] as const,

  /**
   * Global queries (not company-scoped)
   * Platform-wide data that's the same for all tenants
   */
  global: {
    all: ["global"] as const,
    appFeatures: () => buildKey(queryKeys.global.all, "appFeatures"),
    marketingSeoContext: () =>
      buildKey(queryKeys.global.all, "marketingSeoContext"),
    marketingSeoCompetitors: () =>
      buildKey(queryKeys.global.all, "marketingSeoCompetitors"),
    // The caller's own live user (GET /users/me). Not company-scoped — it IS the
    // identity/permission source the whole session projects from. Keyed by the
    // session user id so a prior login's cache can't be read into a new session.
    navigationPreferences: (userId?: string) =>
      buildKey(queryKeys.global.all, "navigationPreferences", userId),
    currentUser: (userId?: string) =>
      buildKey(queryKeys.global.all, "currentUser", userId),
    // Live human-call transcript turns for a conversation (SD-16076). Keyed by
    // conversation id alone, NOT company-scoped: the REST route is
    // GET /conversations/{id}/live-transcript, and the `transcript.updated` SSE
    // nudge carries only the conversation id — so the stream invalidation must
    // be able to target this key from that id alone.
    conversationLiveTranscript: (conversationId: string) =>
      buildKey(
        queryKeys.global.all,
        "conversationLiveTranscript",
        conversationId,
      ),
    // Help center / Knowledge Base articles (SD-9533). Platform-wide, not
    // company-scoped — the same content serves every tenant.
    knowledgeBaseArticles: () =>
      buildKey(queryKeys.global.all, "knowledgeBaseArticles"),
    // Popularity ranking (article slugs by PostHog view count). Separate key
    // from the article list: it refreshes on a different cadence and must not
    // invalidate when article content mutates.
    popularKnowledgeBaseArticles: () =>
      buildKey(queryKeys.global.all, "popularKnowledgeBaseArticles"),
    integrationsCatalog: () =>
      buildKey(queryKeys.global.all, "integrationsCatalog"),
    // Sellable Stripe prices (SD-12933). Global, not account-scoped: the route
    // is GET /accounts/billing/stripe/catalog and the same catalog serves every
    // account being signed up.
    stripeCatalog: () => buildKey(queryKeys.global.all, "stripeCatalog"),
    quoteDecisionAccess: () =>
      buildKey(queryKeys.global.all, "quoteDecisionAccess"),
    industrySettings: () => buildKey(queryKeys.global.all, "industrySettings"),
    industryServices: (type: string) =>
      buildKey(queryKeys.global.all, "industryServices", type),
    voices: () => buildKey(queryKeys.global.all, "voices"),
    holdMusicTracks: () => buildKey(queryKeys.global.all, "holdMusicTracks"),
    agents: () => buildKey(queryKeys.global.all, "agents"),
    agentConfigOptions: () =>
      buildKey(queryKeys.global.all, "agentConfigOptions"),
    // Global agent-config templates (company-less rows in `agents`). Not under
    // a company prefix: the same set is served to every super regardless of the
    // company currently scoped in the shell.
    agentTemplates: () => buildKey(queryKeys.global.all, "agentTemplates"),
    agentTemplate: (agentId: string) =>
      buildKey(queryKeys.global.all, "agentTemplate", agentId),
    agentPromptVersions: (agentId: string, channel: string) =>
      buildKey(queryKeys.global.all, "agentPromptVersions", agentId, channel),
    agentPromptVersion: (
      agentId: string,
      channel: string,
      versionNumber: number,
    ) =>
      buildKey(
        queryKeys.global.all,
        "agentPromptVersion",
        agentId,
        channel,
        String(versionNumber),
      ),
    semanticSearch: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.global.all,
        "semanticSearch",
        deepStableSerialize(filters),
      ),
    promptSectionNames: () =>
      buildKey(queryKeys.global.all, "promptSectionNames"),
    promptSectionTemplates: (section: string) =>
      buildKey(queryKeys.global.all, "promptSectionTemplates", section),
    // Workflow templates (global, no company - super/admin only)
    workflowTemplates: () =>
      buildKey(queryKeys.global.all, "workflowTemplates"),
    // Settings by identifier (used by useGetSettingsIdentifier)
    settings: (identifier: string) => ["settings", identifier] as const,
    // Insights overview (used by Insights page - custom key structure)
    insightsOverview: () => ["insights", "overview"] as const,
    // Revenue report (Insights Revenue tab - custom key structure)
    insightsRevenue: () => ["insights", "revenue"] as const,
    // Financials report (Insights Financials tab - QuickBooks-sourced)
    insightsFinancials: () => ["insights", "financials"] as const,
    // Finance-access manager (CEO grant/revoke of the Financials tab)
    insightsFinanceAccess: () => ["insights", "finance-access"] as const,
    // Churn Radar snapshot (super-admin only). Filters are appended by the
    // feature hook so distinct queue views retain their own cache entry.
    insightsChurnRadar: () => ["insights", "churn-radar"] as const,
    // Voice-runtime call performance (staff only). Filters appended by the hook.
    insightsCallPerformance: () => ["insights", "call-performance"] as const,
    // Industry insights (used by Industry Insights page - custom key structure)
    industryInsights: () => ["insights", "industries"] as const,
    // Internal product-usage metrics read back out of PostHog (staff only)
    productAnalytics: () => ["product-analytics"] as const,
    // Integration adoption census time-series (staff only, per date range)
    integrationAdoption: (range: { startDate?: string; endDate?: string }) =>
      ["product-analytics", "integration-adoption", range] as const,
    // Voice providers (global)
    voiceProviders: () => buildKey(queryKeys.global.all, "voiceProviders"),
    voiceProviderSettings: (provider: string) =>
      buildKey(queryKeys.global.all, "voiceProviderSettings", provider),
    voiceModels: (provider: string) =>
      buildKey(queryKeys.global.all, "voiceModels", provider),
    // Selectable provider-qualified Full-Duplex voices. Global because the
    // native Gemini/OpenAI voice sets are identical for every tenant.
    fullDuplexVoices: () => buildKey(queryKeys.global.all, "fullDuplexVoices"),
    deepgramVoices: () => buildKey(queryKeys.global.all, "deepgramVoices"),
    // Settings prefix for broad invalidation
    settingsPrefix: () => ["settings"] as const,
    // Full-Duplex global settings (bespoke GET/PATCH /settings/full-duplex)
    fullDuplexSettings: () =>
      buildKey(queryKeys.global.all, "fullDuplexSettings"),
    keywordUsage: () => buildKey(queryKeys.global.all, "keywordUsage"),
    // Other global
    availableLlmModels: () =>
      buildKey(queryKeys.global.all, "availableLlmModels"),
    copilotLlmProfiles: () =>
      buildKey(queryKeys.global.all, "copilotLlmProfiles"),
    campaignConfigOptions: () =>
      buildKey(queryKeys.global.all, "campaignConfigOptions"),
    accountsDirectory: (name?: string) =>
      buildKey(queryKeys.global.all, "accountsDirectory", name ?? ""),
    /**
     * Super-only sales-quote directory. Unkeyed by search term: like the
     * accounts directory it caches the whole list and filters in `select`, so a
     * keystroke must not swap caches.
     */
    quotesDirectory: () => buildKey(queryKeys.global.all, "quotesDirectory"),
    quoteOnboardingSettings: (sessionIdentity: string) =>
      buildKey(
        queryKeys.global.all,
        "quoteOnboardingSettings",
        sessionIdentity,
      ),
    publicQuoteOnboarding: (token: string) =>
      buildKey(queryKeys.global.all, "publicQuoteOnboarding", token),
    publicQuoteOnboardingSlots: (
      token: string,
      date: string,
      timeZone: string,
    ) =>
      buildKey(
        queryKeys.global.all,
        "publicQuoteOnboardingSlots",
        token,
        date,
        timeZone,
      ),
    // SD-8216: current super-only bulk voice-AI-provider switch run.
    // Cached by GET /companies/voice-ai-provider-bulk-runs/current and
    // written-through by both the SSE subscription (progress events) and
    // the mutation that kicks off a new run (202 response).
    bulkProviderSwitchCurrent: () =>
      buildKey(queryKeys.global.all, "bulkProviderSwitchCurrent"),
    // SD-8829: current super-only geotable sync run. Cached by
    // GET /geotable/sync/current and written-through by the SSE subscription
    // (progress events) and the mutation that kicks off a new run (202).
    geotableSyncCurrent: () =>
      buildKey(queryKeys.global.all, "geotableSyncCurrent"),
  },

  /**
   * Account-level queries (user's account, spans companies)
   */
  account: (accountId: string) => ({
    all: ["account", accountId] as const,
    details: () => buildKey(queryKeys.account(accountId).all, "details"),
    companies: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.account(accountId).all,
        "companies",
        deepStableSerialize(filters),
      ),
    // Infinite (page-accumulating) companies list. Nested under `companies` so
    // mutation invalidations that target `.companies()` also clear it.
    companiesInfinite: (filters?: QueryKeyFilters) =>
      buildKey(
        queryKeys.account(accountId).companies(),
        "infinite",
        deepStableSerialize(filters),
      ),
    brands: () => buildKey(queryKeys.account(accountId).all, "brands"),
    phoneNumbers: () =>
      buildKey(queryKeys.account(accountId).all, "phoneNumbers"),
    messagingReadiness: () =>
      buildKey(queryKeys.account(accountId).all, "messagingReadiness"),
    portOrders: () => buildKey(queryKeys.account(accountId).all, "portOrders"),
    portOrder: (orderId: string) =>
      buildKey(queryKeys.account(accountId).portOrders(), orderId),
    portOrderAllowedFocWindows: (orderId: string) =>
      buildKey(
        queryKeys.account(accountId).portOrder(orderId),
        "allowedFocWindows",
      ),
    phoneSeats: () => buildKey(queryKeys.account(accountId).all, "phoneSeats"),
    phoneSeatSetups: () =>
      buildKey(queryKeys.account(accountId).all, "phoneSeatSetups"),
    phoneRoutes: () =>
      buildKey(queryKeys.account(accountId).all, "phoneRoutes"),
    phoneNumberAssignmentOperation: (operationId: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "phoneNumberAssignments",
        operationId,
      ),
    phoneSeat: (seatId: string) =>
      buildKey(queryKeys.account(accountId).phoneSeats(), seatId),
    myPhoneSeat: () =>
      buildKey(queryKeys.account(accountId).all, "myPhoneSeat"),
    myPhoneLines: () =>
      buildKey(queryKeys.account(accountId).all, "myPhoneLines"),
    phoneQueues: () =>
      buildKey(queryKeys.account(accountId).all, "phoneQueues"),
    phoneQueue: (queueId: string) =>
      buildKey(queryKeys.account(accountId).phoneQueues(), queueId),
    phoneDashboard: () =>
      buildKey(queryKeys.account(accountId).all, "phoneDashboard"),
    // A sibling, not a child, of the live key: the live key's SSE nudges and
    // take-next invalidations must not re-run a 60-day aggregation.
    phoneDashboardHistory: (params: PhoneDashboardHistoryParams, tz: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "phoneDashboardHistory",
        deepStableSerialize(params),
        tz,
      ),
    phoneCallsRoot: () =>
      buildKey(queryKeys.account(accountId).all, "phoneCalls"),
    // Filters are part of the key (stringified — buildKey takes string
    // segments) so each filter combination caches its own page chain
    // (infinite query, SD-11756). The shared root lets live events refresh
    // every cached filter without widening to unrelated account queries.
    phoneCalls: (filters: CallHistoryFilters) =>
      buildKey(
        queryKeys.account(accountId).phoneCallsRoot(),
        JSON.stringify(filters),
      ),
    phoneCallResolution: (sessionId: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "phoneCallResolution",
        sessionId,
      ),
    // SD-16278 — one finished call's routing timeline. Provider evidence is
    // role-dependent, so isolate immutable timelines by viewer identity and
    // role rather than reusing a staff response after an auth change.
    phoneCallTimeline: (sessionId: string, viewerKey: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "phoneCallTimeline",
        sessionId,
        viewerKey,
      ),
    phoneCallResolutionByControlId: (callControlId: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "phoneCallResolutionByControlId",
        callControlId,
      ),
    copilotTools: (sessionId: string) =>
      buildKey(queryKeys.account(accountId).all, "copilotTools", sessionId),
    serviceTitanCopilotContext: (sessionId: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "serviceTitanCopilotContext",
        sessionId,
      ),
    phoneParked: () =>
      buildKey(queryKeys.account(accountId).all, "phoneParked"),
    // SD-17708 — the account's blocked callers. Account-scoped, not
    // company-scoped: one list covers every company on the account.
    blockedCallers: () =>
      buildKey(queryKeys.account(accountId).all, "blockedCallers"),
    recordingPolicy: () =>
      buildKey(queryKeys.account(accountId).all, "recordingPolicy"),
    holdMusic: () => buildKey(queryKeys.account(accountId).all, "holdMusic"),
    availablePhoneNumbers: () =>
      buildKey(queryKeys.account(accountId).all, "availablePhoneNumbers"),
    areaCodes: () => buildKey(queryKeys.account(accountId).all, "areaCodes"),
    // Account-wide report aggregate (SD-10158): one account-scoped call per window,
    // keyed by the same `reportWindowKey` (preset or custom range) the company-local
    // report uses. `all` is the subtree prefix so a mutation can invalidate every
    // cached window at once.
    reports: () => ({
      all: buildKey(queryKeys.account(accountId).all, "reports"),
      window: (windowKey: string) =>
        buildKey(queryKeys.account(accountId).reports().all, windowKey),
      // New-classification v2 account-wide aggregate (SD-10758 / SD-11016). Nested under
      // the same `reports()` subtree so an override's `reports().all` invalidation
      // busts every cached v2 window too (folded G6).
      windowV2: (windowKey: string) =>
        buildKey(queryKeys.account(accountId).reports().all, "v2", windowKey),
    }),
    campaignPreview: (brandId: string, fallbackPhone?: string) =>
      buildKey(
        queryKeys.account(accountId).all,
        "campaignPreview",
        brandId,
        fallbackPhone ?? "",
      ),
    // Billing (SD-9851): account-scoped, one account = one invoice. Grouped
    // under a `billing` prefix so the whole surface can be invalidated at once.
    billing: () => ({
      all: buildKey(queryKeys.account(accountId).all, "billing"),
      config: () =>
        buildKey(queryKeys.account(accountId).billing().all, "config"),
      summary: () =>
        buildKey(queryKeys.account(accountId).billing().all, "summary"),
      invoices: () =>
        buildKey(queryKeys.account(accountId).billing().all, "invoices"),
      // Stripe signup state (SD-12933): GET /accounts/{id}/billing/stripe.
      // Under `billing` so a refresh-from-Stripe can bust the whole surface.
      stripe: () =>
        buildKey(queryKeys.account(accountId).billing().all, "stripe"),
    }),
    // Account-wide Coach (SD-14223): the same bundled / agent-performance /
    // team-performance dashboards the company routes serve, aggregated across
    // every connected company on the account. `all` is the subtree prefix for
    // invalidating every cached dashboard window at once.
    coach: () => ({
      all: buildKey(queryKeys.account(accountId).all, "coach"),
      bundledDashboard: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.account(accountId).coach().all,
          "bundledDashboard",
          deepStableSerialize(filters),
        ),
      agentPerformance: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.account(accountId).coach().all,
          "agentPerformance",
          deepStableSerialize(filters),
        ),
      teamPerformance: (filters?: QueryKeyFilters) =>
        buildKey(
          queryKeys.account(accountId).coach().all,
          "teamPerformance",
          deepStableSerialize(filters),
        ),
      // CSR agent-profiles (SD-14690): combine/separate write routes. Nested
      // under `.all` so a mutation's `coach().all` invalidation also busts any
      // future profile reads, and a combine/separate re-derives every roster.
      agentProfiles: () =>
        buildKey(queryKeys.account(accountId).coach().all, "agentProfiles"),
      // Advisory identity-match suggestions (SD-14691). Nested under `.all` so a
      // combine/separate invalidation also drops accepted/stale suggestions.
      identitySuggestions: () =>
        buildKey(
          queryKeys.account(accountId).coach().all,
          "identitySuggestions",
        ),
    }),
  }),

  /**
   * The signed-in user's own pending phone-seat invitation (SD-15882).
   *
   * Session-scoped, not account-scoped, on purpose: an invitee may not know
   * which account invited them, and the server resolves it from the token. It
   * therefore cannot hang off `account(accountId)` like `myPhoneSeat` does.
   */
  myPhoneSeatInvite: (userId?: string) =>
    (userId
      ? ["myPhoneSeatInvite", userId]
      : ["myPhoneSeatInvite"]) as readonly string[],
} as const;
